import { inject, Injectable } from "@angular/core";
import { Session, SupabaseClient, User } from "@supabase/supabase-js";
import {
    BehaviorSubject,
    from,
    Observable,
    of,
    shareReplay,
    switchMap,
} from "rxjs";
import { catchError, map } from "rxjs/operators";

import { SupabaseService } from "src/app/core/supabase.service";
import { Profile } from "src/app/events/models/event.models";

/** The extra registration-form fields, forwarded to Postgres via signUp's user metadata. */
export interface RegisterProfileDetails {
    birthDate?: string | null;
    phoneNumber?: string | null;
    isStudent?: boolean;
    campus?: string | null;
    otherCampus?: string | null;
    studyProgram?: string | null;
    academicYear?: string | null;
    subscribeNewsletter?: boolean;
}

export interface AuthResult {
    user: User | null;
    /** A message safe to show the user, or null on success. */
    error: string | null;
    /** signUp only: the account exists but the address must be confirmed first. */
    needsEmailConfirmation?: boolean;
}

/**
 * Supabase returns terse, technical strings. Turn the ones people actually
 * hit into something readable, and keep the rest generic.
 */
export function describeAuthError(error: unknown): string {
    const raw = (
        (error as { message?: string })?.message ?? String(error ?? "")
    ).toLowerCase();

    if (raw.includes("invalid login credentials")) {
        return "That email and password don't match an account.";
    }
    if (raw.includes("email not confirmed")) {
        return "Your email address hasn't been confirmed yet. Check your inbox for the confirmation link we sent when you signed up.";
    }
    if (raw.includes("user already registered") || raw.includes("already been registered")) {
        return "An account with that email already exists. Try signing in instead.";
    }
    if (raw.includes("password should be at least")) {
        return "That password is too short.";
    }
    if (raw.includes("rate limit") || raw.includes("too many requests")) {
        return "Too many attempts. Please wait a minute and try again.";
    }
    if (raw.includes("failed to fetch") || raw.includes("networkerror")) {
        return "Couldn't reach the server. Check your connection and try again.";
    }
    return "Something went wrong signing you in. Please try again.";
}

@Injectable({ providedIn: "root" })
export class AuthenticationService {
    private readonly supabase: SupabaseClient = inject(SupabaseService).client;
    private readonly sessionSubject = new BehaviorSubject<Session | null>(null);

    /** The signed-in user, or null. */
    readonly user$: Observable<User | null> = this.sessionSubject.pipe(
        map((session) => session?.user ?? null),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    /** The signed-in user's profile row (carries the admin role), or null. */
    readonly profile$: Observable<Profile | null> = this.user$.pipe(
        switchMap((user) => (user ? this.fetchProfile(user.id) : of(null))),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    /** True when the signed-in user has admin rights (role 'admin' or 'board' — see is_admin() in Postgres). */
    readonly isAdmin$: Observable<boolean> = this.profile$.pipe(
        map((profile) => profile?.role === "admin" || profile?.role === "board"),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    constructor() {
        this.supabase.auth.getSession().then(({ data }) => {
            this.sessionSubject.next(data.session);
        });

        this.supabase.auth.onAuthStateChange((_event, session) => {
            this.sessionSubject.next(session);
        });
    }

    async signInEmail(email: string, password: string): Promise<AuthResult> {
        try {
            const { data, error } = await this.supabase.auth.signInWithPassword({
                email: email.trim(),
                password,
            });
            if (error) {
                console.error("authentication error:", error);
                return { user: null, error: describeAuthError(error) };
            }
            return { user: data.user, error: null };
        } catch (error) {
            console.error("authentication error:", error);
            return { user: null, error: describeAuthError(error) };
        }
    }

    async registerUser(
        email: string,
        password: string,
        fullName?: string,
        profile?: RegisterProfileDetails
    ): Promise<AuthResult> {
        try {
            // Everything here rides in on auth.users.raw_user_meta_data and
            // is copied into public.profiles by the on_auth_user_created
            // trigger (see supabase/migrations/0005_member_profile_fields.sql)
            // — signUp() itself never touches the profiles table directly.
            const metadata: Record<string, unknown> = {};
            if (fullName) {
                metadata["full_name"] = fullName;
            }
            if (profile) {
                if (profile.birthDate) metadata["birth_date"] = profile.birthDate;
                if (profile.phoneNumber) metadata["phone_number"] = profile.phoneNumber;
                metadata["is_student"] = !!profile.isStudent;
                if (profile.campus) metadata["campus"] = profile.campus;
                if (profile.otherCampus) metadata["other_campus"] = profile.otherCampus;
                if (profile.studyProgram) metadata["study_program"] = profile.studyProgram;
                if (profile.academicYear) metadata["academic_year"] = profile.academicYear;
                metadata["subscribe_newsletter"] = !!profile.subscribeNewsletter;
            }

            const { data, error } = await this.supabase.auth.signUp({
                email: email.trim(),
                password,
                options: Object.keys(metadata).length ? { data: metadata } : undefined,
            });
            if (error) {
                console.error("authentication error:", error);
                return { user: null, error: describeAuthError(error) };
            }

            // Supabase doesn't error on signUp for an email that's already
            // registered — to avoid leaking which emails have accounts, it
            // silently returns a "user" with no session and an empty
            // `identities` array instead. That's the only signal we get, so
            // that's what we check for.
            if (data.user && data.user.identities && data.user.identities.length === 0) {
                return {
                    user: null,
                    error: "An account with that email already exists. Try signing in instead.",
                };
            }

            // With email confirmation on, signUp returns a user but no session.
            return {
                user: data.user,
                error: null,
                needsEmailConfirmation: !data.session,
            };
        } catch (error) {
            console.error("authentication error:", error);
            return { user: null, error: describeAuthError(error) };
        }
    }

    async requestPasswordReset(email: string): Promise<{ error: string | null }> {
        try {
            const { error } = await this.supabase.auth.resetPasswordForEmail(email.trim(), {
                redirectTo: `${window.location.origin}/reset-password`,
            });
            if (error) {
                console.error("authentication error:", error);
                return { error: describeAuthError(error) };
            }
            return { error: null };
        } catch (error) {
            console.error("authentication error:", error);
            return { error: describeAuthError(error) };
        }
    }

    async updatePassword(newPassword: string): Promise<{ error: string | null }> {
        try {
            const { error } = await this.supabase.auth.updateUser({ password: newPassword });
            if (error) {
                console.error("authentication error:", error);
                return { error: describeAuthError(error) };
            }
            return { error: null };
        } catch (error) {
            console.error("authentication error:", error);
            return { error: describeAuthError(error) };
        }
    }

    async logout(): Promise<void> {
        await this.supabase.auth.signOut();
    }

    async isLoggedIn(): Promise<boolean> {
        const { data } = await this.supabase.auth.getSession();
        return data.session !== null;
    }

    /** Authoritative admin check — asks Postgres, so it can't be faked client-side. */
    async isAdmin(): Promise<boolean> {
        const { data, error } = await this.supabase.rpc("is_admin");
        if (error) {
            console.error("is_admin check failed:", error);
            return false;
        }
        return data === true;
    }

    currentUserId(): string | null {
        return this.sessionSubject.value?.user?.id ?? null;
    }

    getCurrentUserName(): string {
        const user = this.sessionSubject.value?.user;
        return (user?.user_metadata?.["full_name"] as string) || "";
    }

    getCurrentEmail(): string {
        return this.sessionSubject.value?.user?.email || "";
    }

    private fetchProfile(userId: string): Observable<Profile | null> {
        return from(
            this.supabase
                .from("profiles")
                .select("*")
                .eq("id", userId)
                .maybeSingle()
        ).pipe(
            map(({ data }) => (data as Profile) ?? null),
            catchError(() => of(null))
        );
    }
}
